import { TpOrganizationInfo } from '../../../api/models/admin';
import { UserOrganization } from '../../../api/models/apis';
import { M2MApi } from '../../../api/models/m2mApi';
import { FORM_DATATYPES, promptAutoForm, TpAutoFormDef } from '../../../components/auto-form-modal/AutoForm';
import { promptConfirmationBox, TpConfirmData } from '../../../components/message-boxes/ConfirmBox';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { TpUserInfo } from '../../../data/interfaces';

export function promptCreateUser(
  m2mApi: M2MApi,
  organizations: TpOrganizationInfo[],
  activeOrganization: TpOrganizationInfo = {} as TpOrganizationInfo,
): Promise<TpUserInfo> {
  const form: TpAutoFormDef = {
    title: `Create new User`,
    fields: [
      {
        id: 'email',
        name: 'Email',
        dataType: FORM_DATATYPES.STRING,
        startContent: '',
        inputValidation: (content) => {
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(content);
        },
        ValidationMessage: 'invalid email adress',
        required: true,
      },
      {
        id: 'isOrganizationAdmin',
        name: 'Organization Admin',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'canWrite',
        name: 'Can Write',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
    ],
  };

  if (Object.keys(activeOrganization).length == 0) {
    form.fields.push({
      id: 'organizationName',
      name: 'Organization',
      dataType: FORM_DATATYPES.MULTIPLECHOICE,
      startContent: organizations[0].name,
      choices: organizations.map((org) => {
        return {
          id: org.name,
          content: org.name,
        };
      }),
      required: true,
    });
  }

  return promptAutoForm(form).then((dataForm: any) => {
    const newUserInfo: TpUserInfo = {
      email: dataForm.email,
      isOrganizationAdmin: dataForm.isOrganizationAdmin,
      canWrite: dataForm.canWrite,
      organizations: [],
    };
    const organizationName =
      Object.keys(activeOrganization).length == 0 ? dataForm.organizationName : activeOrganization.name;
    return m2mApi.systemAdmin.createUser(newUserInfo, organizationName).then((resp) => {
      return resp;
    });
  });
}

export function promptModifyUser(
  m2mApi: M2MApi,
  userInfo: TpUserInfo,
  activeOrganization: UserOrganization,
): Promise<TpUserInfo> {
  const form: TpAutoFormDef = {
    title: `Modify User for ${activeOrganization.organizationName}`,
    fields: [
      {
        id: 'canWrite',
        name: 'Can Write',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: activeOrganization.canWrite,
        required: false,
      },
      {
        id: 'isOrganizationAdmin',
        name: 'Organization Admin',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: activeOrganization.isOrganizationAdmin,
        required: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const request = { ...activeOrganization };
    request.isOrganizationAdmin = dataForm.isOrganizationAdmin;
    request.canWrite = dataForm.canWrite;
    return m2mApi.systemAdmin.modifyUser(userInfo, request).then((resp) => {
      activeOrganization.canWrite = resp.canWrite;
      activeOrganization.isOrganizationAdmin = resp.isOrganizationAdmin;
      return resp;
    });
  });
}

export function promptAddUserToOrganization(
  m2mApi: M2MApi,
  userInfo: TpUserInfo,
  organizations: TpOrganizationInfo[],
): Promise<void> {
  const form: TpAutoFormDef = {
    title: `Add User to Organization`,
    fields: [
      {
        id: 'organizationName',
        name: 'Organization',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: '',
        choices: organizations.map((org) => {
          return {
            id: org.name,
            content: org.name,
          };
        }),
        required: true,
      },
      {
        id: 'isOrganizationAdmin',
        name: 'Organization Admin',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'canWrite',
        name: 'Can Write',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const newUserOrganization: UserOrganization = {
      organizationId: '',
      organizationName: dataForm.organizationName,
      isActive: false,
      canWrite: dataForm.canWrite,
      isOrganizationAdmin: dataForm.isOrganizationAdmin,
    };
    return m2mApi.systemAdmin.addUserToOrganization(userInfo, newUserOrganization);
  });
}

export function promptAddUserToSelectedOrganization(
  m2mApi: M2MApi,
  users: TpUserInfo[],
  organization: TpOrganizationInfo,
): Promise<void> {
  const form: TpAutoFormDef = {
    title: `Add User to ${organization.name}`,
    fields: [
      {
        id: 'userEmail',
        name: 'Email',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: '',
        choices: users.map((usr) => {
          return {
            id: usr.email as string,
            content: usr.email as string,
          };
        }),
        required: true,
      },
      {
        id: 'isOrganizationAdmin',
        name: 'Organization Admin',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
      {
        id: 'canWrite',
        name: 'Can Write',
        dataType: FORM_DATATYPES.CHECKBOX,
        startContent: false,
        required: false,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const selectedUserInfo = users.find((usr) => usr.email === dataForm.userEmail);
    const newUserOrganization: UserOrganization = {
      organizationId: '',
      organizationName: organization.name,
      isActive: false,
      canWrite: dataForm.canWrite,
      isOrganizationAdmin: dataForm.isOrganizationAdmin,
    };
    if (selectedUserInfo) {
      return m2mApi.systemAdmin.addUserToOrganization(selectedUserInfo, newUserOrganization);
    }
  });
}

export function promptRemoveUserFromOrganization(
  m2mApi: M2MApi,
  userInfo: TpUserInfo,
  organization_name: string,
): Promise<void> {
  const confirmData: TpConfirmData = {
    title: `Remove ${userInfo.email} from ${organization_name}`,
    description: `Do you really want to remove ${userInfo.email} from ${organization_name}? \n\n`,
  };

  return promptConfirmationBox(confirmData).then((confirmed) => {
    if (confirmed) {
      return m2mApi.systemAdmin.removeUserFromOrganization(userInfo, organization_name);
    }
  });
}

export function promptReassignUserActiveOrganization(
  m2mApi: M2MApi,
  userInfo: TpUserInfo,
  activeOrganization: UserOrganization,
): Promise<void> {
  const otherOrganizations = userInfo.organizations.filter(
    (organization) => organization.organizationId !== activeOrganization.organizationId,
  );
  if (otherOrganizations.length === 0) {
    showErrorMessage('No other organizations available to reassign the user.');
    return Promise.resolve();
  }

  const form: TpAutoFormDef = {
    title: 'Assign User to other organization',
    fields: [
      {
        id: 'organizationId',
        name: 'Organization',
        dataType: FORM_DATATYPES.MULTIPLECHOICE,
        startContent: otherOrganizations[0].organizationId,
        choices: otherOrganizations.map((organization) => ({
          id: organization.organizationId,
          content: organization.organizationName,
        })),
        required: true,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) =>
    m2mApi.systemAdmin.reassignUserActiveOrganization(
      userInfo,
      activeOrganization.organizationName,
      dataForm.organizationId,
    ),
  );
}

export function promptDeleteUser(m2mApi: M2MApi, userInfo: TpUserInfo): Promise<void> {
  const confirmData: TpConfirmData = {
    title: `Delete ${userInfo.email}`,
    description: `Do you really want to delete ${userInfo.email}? \n\n`,
  };

  return promptConfirmationBox(confirmData).then((confirmed) => {
    if (confirmed) {
      return m2mApi.systemAdmin.deleteUser(userInfo);
    }
  });
}
